/**
 * Correo → código → nombre y avatar.
 *
 * Tres pantallas del onboarding de seis (spec §10), en un solo componente
 * porque comparten estado y el usuario las vive como un solo trámite.
 *
 * Dos decisiones de producto viven aquí:
 *
 * 1. **El nombre se pide ahora, no al publicar.** Diferirlo al `409
 *    profile_incomplete` significa que alguien escribe su primera
 *    recomendación y *después* descubre que no puede publicarla, lo que
 *    `docs/03` §4.1 prohíbe: el estado se comunica antes de escribir.
 * 2. **"No es mi correo" siempre visible.** Equivocarse de correo es la
 *    causa #1 de abandono en cualquier flujo OTP; sin salida, la persona
 *    espera un código que nunca va a llegar.
 */

import { useState } from 'react';
import { DISPLAY_NAME_MIN, OTP_LENGTH } from '@gu/contracts';

import { AVATAR_GALLERY } from '../profile/avatar.js';
import { deviceFingerprint } from './device.js';
import {
  ApiError,
  fetchMe,
  requestOtp,
  saveProfile,
  signInWithOtp,
  type Me,
} from '../../shared/lib/api.js';
import { Boton, Campo, Pantalla, Stack, Texto, Titulo } from '../../shared/ui/primitives.js';

export interface SignupFlowProps {
  /** El pase, ya confirmado con un tap en la landing. */
  inviteCode: string;
  onDone: (perfil: Me) => void;
}

type Paso = 'correo' | 'codigo' | 'perfil';

/**
 * Traduce el error del servidor a algo que se pueda leer sin sentirse
 * acusado. `docs/03` §8 prohíbe las palabras *inválido, denegado, no
 * autorizado, fraude, dispositivo sospechoso*.
 */
function copyDeError(e: unknown): string {
  if (!(e instanceof ApiError)) {
    return 'No pudimos conectar. Revisa tu señal e inténtalo otra vez.';
  }

  switch (e.code) {
    case 'otp_rate_limited':
      return 'Pediste varios códigos seguidos. Espera unos minutos y vuelve a intentar.';
    case 'invite_same_device':
      // El delicado: la regla lo bloquea, la copy no puede acusarlo.
      return 'Este pase es para alguien más. Se abrió desde el mismo teléfono que lo mandó — pásaselo a quien va dirigido y que lo abra en el suyo.';
    case 'invite_already_redeemed':
      return 'Alguien ya entró con este pase. Pídele otro a quien te invitó: se le repone solo.';
    case 'invite_expired':
      return 'Ese pase venció. Pídele otro a quien te invitó.';
    case 'invite_required':
      return 'Necesitas una invitación para entrar.';
    case 'INVALID_OTP':
    case 'otp_invalid':
      return 'Ese código no coincide. Revísalo y vuelve a intentar.';
    case 'otp_expired':
      return 'Ese código ya venció. Pide uno nuevo.';
    default:
      return e.message || 'Algo salió mal de nuestro lado.';
  }
}

export function SignupFlow({ inviteCode, onDone }: SignupFlowProps) {
  const [paso, setPaso] = useState<Paso>('correo');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [nombre, setNombre] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function pedirCodigo() {
    // Validación mínima a propósito: la regla real la tiene el servidor y
    // aquí solo se evita el viaje obvio. Un regex estricto de correo
    // rechaza direcciones válidas y raras que sí existen.
    if (!email.includes('@') || !email.includes('.')) {
      setError('Ese correo se ve incompleto. Revísalo.');
      return;
    }

    setOcupado(true);
    setError(null);
    try {
      await requestOtp(email);
      setPaso('codigo');
    } catch (e) {
      setError(copyDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  async function entrar() {
    setOcupado(true);
    setError(null);
    try {
      await signInWithOtp({
        email,
        otp,
        inviteCode,
        // De esta huella depende el candado I2 del servidor.
        deviceFp: deviceFingerprint(),
      });

      const perfil = await fetchMe();
      // Si ya tiene nombre y cara, no hay nada que pedirle.
      if (perfil?.canPublish) onDone(perfil);
      else setPaso('perfil');
    } catch (e) {
      setError(copyDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  async function guardarPerfil() {
    if (nombre.trim().length < DISPLAY_NAME_MIN || !avatar) return;

    setOcupado(true);
    setError(null);
    try {
      onDone(await saveProfile({ displayName: nombre.trim(), avatarUrl: avatar }));
    } catch (e) {
      setError(copyDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Pantalla>
      {paso === 'correo' && (
        <Stack gap="var(--gu-space-5)">
          <Titulo variante="display">¿A qué correo te mandamos la llave?</Titulo>
          <Texto variante="secundario">
            Es la única forma de entrar. Ni contraseñas, ni nada que recordar.
          </Texto>

          <Campo
            id="email"
            etiqueta="Tu correo"
            tipo="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            valor={email}
            onChange={setEmail}
            error={error ?? undefined}
          />

          <Boton onClick={() => void pedirCodigo()} disabled={ocupado}>
            {ocupado ? 'Enviando…' : 'Enviar mi llave'}
          </Boton>
        </Stack>
      )}

      {paso === 'codigo' && (
        <Stack gap="var(--gu-space-5)">
          <Titulo variante="display">Revisa tu correo.</Titulo>
          <Texto variante="secundario">
            Te mandamos {OTP_LENGTH} dígitos a <strong>{email}</strong>.
          </Texto>

          <Campo
            id="otp"
            etiqueta="El código"
            // `numeric` y no `number`: el spinner de number en móvil es
            // inútil y el campo acepta pegar con espacios.
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={OTP_LENGTH}
            autoFocus
            destacado
            valor={otp}
            onChange={(v) => setOtp(v.replace(/\D/g, ''))}
            error={error ?? undefined}
          />

          <Boton onClick={() => void entrar()} disabled={ocupado}>
            {ocupado ? 'Entrando…' : 'Entrar'}
          </Boton>

          {/* La salida de emergencia: causa #1 de abandono en flujos OTP. */}
          <Boton
            variante="secundario"
            onClick={() => {
              setPaso('correo');
              setOtp('');
              setError(null);
            }}
          >
            No es mi correo
          </Boton>
        </Stack>
      )}

      {paso === 'perfil' && (
        <Stack gap="var(--gu-space-5)">
          <Titulo variante="display">Ya estás dentro. ¿Quién eres?</Titulo>
          <Texto variante="secundario">
            Aquí nadie recomienda de forma anónima: tu nombre y tu cara van
            junto a lo que escribas. Así es como esto vale algo.
          </Texto>

          <Campo
            id="nombre"
            etiqueta="¿Cómo te llamas?"
            autoComplete="nickname"
            autoFocus
            valor={nombre}
            onChange={setNombre}
            error={error ?? undefined}
          />

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend
              style={{
                fontSize: 'var(--gu-text-body-sm)',
                color: 'var(--gu-text-secondary)',
                fontWeight: 550,
                padding: 0,
              }}
            >
              Elige tu cara
            </legend>
            <div
              style={{
                display: 'grid',
                /*
                 * `auto-fill` y no tres columnas fijas: con 15 avatares, un
                 * teléfono ancho aprovecha cuatro y uno estrecho baja a
                 * tres sin que nada se apriete.
                 */
                gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
                gap: 'var(--gu-space-3)',
                marginTop: 'var(--gu-space-3)',
              }}
            >
              {AVATAR_GALLERY.map((a) => (
                <label
                  key={a.id}
                  style={{
                    display: 'block',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    aspectRatio: '1',
                    /* El seleccionado se marca con un anillo del color de
                       marca, no con un borde fino que se pierde. */
                    outline:
                      avatar === a.src
                        ? '3px solid var(--gu-primary-strong)'
                        : '1px solid var(--gu-border-subtle)',
                    outlineOffset: avatar === a.src ? '2px' : '0',
                    transition: 'outline-color var(--gu-duration-fast) var(--gu-ease-standard)',
                  }}
                >
                  <input
                    type="radio"
                    name="avatar"
                    value={a.src}
                    checked={avatar === a.src}
                    onChange={() => setAvatar(a.src)}
                    /* Fuera de pantalla, no `display:none`: así sigue
                       siendo enfocable con teclado y anunciable. */
                    style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
                  />
                  <img
                    src={a.src}
                    alt={a.alt}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: '50%',
                    }}
                  />
                </label>
              ))}
            </div>
          </fieldset>

          <Boton
            onClick={() => void guardarPerfil()}
            disabled={ocupado || nombre.trim().length < DISPLAY_NAME_MIN || !avatar}
          >
            Listo
          </Boton>
        </Stack>
      )}
    </Pantalla>
  );
}






